// crewbit-admin/src/components/schedule/DayHeader.tsx
//
// Schedule Module Phase C C1.5 — date label + nav arrows for the Day canvas.
// Body parallel to mobile crewbit-mobile/components/schedule/DayHeader.tsx
// modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2.3 + §C.6.

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { palette, admin } from '../../styles/tokens';
import type { DayLayoutMode } from '../../lib/scheduleLayout';

export interface DayHeaderProps {
  date: string;
  layoutMode: DayLayoutMode;
  onPrev?: () => void;
  onNext?: () => void;
  onToday?: () => void;
  trailing?: React.ReactNode;
}

function formatDateLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  if (iso === todayIso) return 'Today';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function DayHeader({ date, onPrev, onNext, onToday, trailing }: DayHeaderProps) {
  const label = formatDateLabel(date);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '12px 24px', gap: 16,
      backgroundColor: admin.surface,
      borderBottom: `1px solid ${admin.border}`,
    }}>
      <button
        type="button"
        onClick={onPrev}
        aria-label="Previous day"
        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}
      >
        <ChevronLeft size={20} color={palette.g700} />
      </button>

      <button
        type="button"
        onClick={onToday}
        aria-label={`Day: ${label}`}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 17, fontWeight: 600, color: palette.g800, padding: '4px 8px',
        }}
      >
        {label}
      </button>

      <button
        type="button"
        onClick={onNext}
        aria-label="Next day"
        style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }}
      >
        <ChevronRight size={20} color={palette.g700} />
      </button>

      {trailing ? <div style={{ marginLeft: 'auto' }}>{trailing}</div> : null}
    </div>
  );
}
